import type {QuoteDocumentData, QuoteEstimationUnit, QuoteLineItem, QuoteRollup, QuoteTemplate} from './resolver/types';

export type EstimateRow =
	| {kind: 'phaseHeader'; index: number; label: string; subtotal: number | null}
	| {kind: 'deliverableHeader'; label: string}
	| {
			kind: 'item';
			itemKind: 'role' | 'expense';
			label: string;
			hours: number | null;
			rate: number | null;
			total: number | null;
	  }
	| {kind: 'expensesTotal'; total: number}
	| {kind: 'rollup'; label: string; hours: number | null; total: number};

export type EstimateTotals = {
	/** Sum of role totals — the real amount, regardless of display toggles. */
	subtotal: number;
	/** Sum of expense totals — the real amount, regardless of display toggles. */
	expenses: number;
	/** subtotal + expenses. Display toggles only hide rows, never change the math. */
	grandTotal: number;
	/** True at coarser roll-ups when expenses are folded into the roll-up rows (no separate expenses row). */
	expensesFolded: boolean;
};

export type EstimateModel = {
	rows: EstimateRow[];
	totals: EstimateTotals;
	unit: QuoteEstimationUnit;
	minutesPerDay: number;
	showTime: boolean;
	showRate: boolean;
	firstColumn: QuoteRollup;
};

const FALLBACK_MINUTES_PER_DAY = 480;

const sum = (values: number[]): number => values.reduce((acc, value) => acc + value, 0);
const roleTotals = (items: QuoteLineItem[]): number => sum(items.filter(i => i.kind === 'role').map(i => i.total));
const expenseTotals = (items: QuoteLineItem[]): number => sum(items.filter(i => i.kind === 'expense').map(i => i.total));
const roleHours = (items: QuoteLineItem[]): number => sum(items.filter(i => i.kind === 'role').map(i => i.hours));

/** Groups items by key preserving first-appearance order. */
const groupInOrder = <K>(items: QuoteLineItem[], keyOf: (item: QuoteLineItem) => K): Map<K, QuoteLineItem[]> => {
	const groups = new Map<K, QuoteLineItem[]>();
	for (const item of items) {
		const key = keyOf(item);
		const group = groups.get(key);
		if (group) {
			group.push(item);
		} else {
			groups.set(key, [item]);
		}
	}
	return groups;
};

/** "12h" / "1.5d" — days use the baseline's minutes-per-day, to at most one decimal. */
export const formatEstimateHours = (hours: number, unit: QuoteEstimationUnit, minutesPerDay: number): string => {
	if (unit !== 'days') {
		return `${Math.round(hours * 100) / 100}h`;
	}
	const hoursPerDay = (minutesPerDay > 0 ? minutesPerDay : FALLBACK_MINUTES_PER_DAY) / 60;
	const days = hours / hoursPerDay;
	const rounded = Math.round(days * 10) / 10;
	return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}d`;
};

/** Per-hour rate expressed in the display unit: unchanged for hours, × hours-per-day for days. */
export const ratePerUnit = (ratePerHour: number, unit: QuoteEstimationUnit, minutesPerDay: number): number => {
	if (unit !== 'days') return ratePerHour;
	return ratePerHour * ((minutesPerDay > 0 ? minutesPerDay : FALLBACK_MINUTES_PER_DAY) / 60);
};

const buildRoleRollupRows = (data: QuoteDocumentData, template: QuoteTemplate): EstimateRow[] => {
	const rows: EstimateRow[] = [];
	const phases = groupInOrder(data.lineItems, item => item.phaseId);
	let phaseIndex = 0;
	phases.forEach(items => {
		phaseIndex += 1;
		rows.push({
			kind: 'phaseHeader',
			index: phaseIndex,
			label: items[0].phaseName,
			subtotal: template.showPhaseSubtotals ? roleTotals(items) : null,
		});
		const showDeliverableHeaders = data.useDeliverables && template.showDeliverables;
		const deliverableGroups = groupInOrder(
			items.filter(item => item.kind === 'role'),
			item => item.deliverableId
		);
		deliverableGroups.forEach((groupItems, deliverableId) => {
			if (showDeliverableHeaders && deliverableId != null && groupItems[0].deliverableName) {
				rows.push({kind: 'deliverableHeader', label: groupItems[0].deliverableName});
			}
			for (const item of groupItems) {
				rows.push({
					kind: 'item',
					itemKind: 'role',
					label: item.name,
					hours: item.hours,
					rate: item.rate,
					total: template.showRoleTotals ? item.total : null,
				});
			}
		});
		const expenses = items.filter(item => item.kind === 'expense');
		if (template.showPhaseExpenses && expenses.length > 0) {
			for (const expense of expenses) {
				rows.push({
					kind: 'item',
					itemKind: 'expense',
					label: expense.name,
					hours: null,
					rate: null,
					total: expense.total,
				});
			}
			rows.push({kind: 'expensesTotal', total: expenseTotals(expenses)});
		}
	});
	return rows;
};

const buildGroupedRollupRows = (
	data: QuoteDocumentData,
	template: QuoteTemplate,
	keyOf: (item: QuoteLineItem) => string,
	labelOf: (items: QuoteLineItem[]) => string
): EstimateRow[] => {
	const rows: EstimateRow[] = [];
	groupInOrder(data.lineItems, keyOf).forEach(items => {
		rows.push({
			kind: 'rollup',
			label: labelOf(items),
			hours: roleHours(items),
			total: roleTotals(items) + (template.showPhaseExpenses ? expenseTotals(items) : 0),
		});
	});
	return rows;
};

/**
 * Computes every estimate row and total from the same frozen line items. Totals always use the
 * real amounts — the display toggles only hide rows.
 */
export const buildEstimate = (data: QuoteDocumentData, template: QuoteTemplate): EstimateModel => {
	const subtotal = roleTotals(data.lineItems);
	const expenses = expenseTotals(data.lineItems);
	const rollup: QuoteRollup = template.rollup === 'deliverable' && !data.useDeliverables ? 'phase' : template.rollup;

	let rows: EstimateRow[];
	switch (rollup) {
		case 'project':
			rows = [
				{
					kind: 'rollup',
					label: data.projectName,
					hours: roleHours(data.lineItems),
					total: subtotal + (template.showPhaseExpenses ? expenses : 0),
				},
			];
			break;
		case 'phase':
			rows = buildGroupedRollupRows(
				data,
				template,
				item => item.phaseId,
				items => items[0].phaseName
			);
			break;
		case 'deliverable':
			// Items without a deliverable fall back to grouping (and labelling) by their phase.
			rows = buildGroupedRollupRows(
				data,
				template,
				item => (item.deliverableId != null ? `deliverable:${item.deliverableId}` : `phase:${item.phaseId}`),
				items => items[0].deliverableName || items[0].phaseName
			);
			break;
		default:
			rows = buildRoleRollupRows(data, template);
	}

	return {
		rows,
		totals: {
			subtotal,
			expenses,
			grandTotal: subtotal + expenses,
			expensesFolded: rollup !== 'roles' && template.showPhaseExpenses,
		},
		unit: data.estimationUnit,
		minutesPerDay: data.minutesPerDay > 0 ? data.minutesPerDay : FALLBACK_MINUTES_PER_DAY,
		showTime: template.showTime,
		showRate: template.showRate,
		firstColumn: rollup,
	};
};
